'use client';

import { useEffect, useRef } from 'react';

import { Bell } from 'lucide-react';

import { useUserWorkspace } from '@kit/accounts/hooks/use-user-workspace';
import {
  ResponsivePopup,
  ResponsivePopupClose,
  ResponsivePopupContent,
} from '@kit/ui/responsive-popup';

export interface PricingNotifyPopupProps {
  open: boolean;
  onClose: () => void;
}

export function PricingNotifyPopup({ open, onClose }: PricingNotifyPopupProps) {
  const { user } = useUserWorkspace();
  const sentRef = useRef(false);

  useEffect(() => {
    if (!open || sentRef.current) return;
    sentRef.current = true;

    // Fire-and-forget — the user's ID is associated server-side via auth cookies.
    fetch('/api/ev/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events: [
          {
            eventType: 'select',
            drugId: '__pricing_notify__',
            drugName: 'pricing_notify',
          },
        ],
      }),
    }).catch(() => {
      // silently ignore
    });
  }, [open]);

  return (
    <ResponsivePopup
      desktop="dialog"
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <ResponsivePopupContent
        srTitle="Płatności będą dostępne wkrótce"
        overlayClassName="bg-black/40 backdrop-blur-sm"
        className="border-[#d5d0c8] bg-[#fffef9]"
        desktopClassName="max-w-sm rounded-xl border-2 p-6 shadow-[4px_4px_0_0_#d5d0c8]"
      >
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <Bell className="h-5 w-5 text-green-600" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-slate-800">
            Płatności będą dostępne wkrótce
          </h3>
          <p className="mt-2 text-xs text-slate-600">
            {/* No workspace context (and so no email) outside /home, e.g. on /pricing */}
            {user?.email ? (
              <>
                {'Poinformujemy Cię na'}{' '}
                <strong className="font-semibold">{user.email}</strong>
                {', gdy płatności będą dostępne.'}
              </>
            ) : (
              'Poinformujemy Cię e-mailem, gdy płatności będą dostępne.'
            )}
          </p>
          <ResponsivePopupClose asChild>
            <button
              type="button"
              data-test="pricing-notify-close"
              className="mt-5 h-11 w-full rounded-lg border-2 border-[#c8c3b8] bg-[#f7f5ef] px-6 py-2 text-sm font-semibold text-stone-700 shadow-[3px_3px_0_0_#c8c3b8] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0_0_#c8c3b8] sm:h-auto sm:w-auto"
            >
              Zamknij
            </button>
          </ResponsivePopupClose>
        </div>
      </ResponsivePopupContent>
    </ResponsivePopup>
  );
}
